import { PrimitiveAtom, useSetAtom } from 'jotai';
import { Alert, AlertColor } from '@mui/material';
import { SnackbarMessage } from '@/types';
import { snackbarMessage as TemplateBuilderSnackbarMessage } from './state';
import {
  RemediationScoreConfig,
  RemediationScoreAreaField,
  RemediationScorePoiField,
  RemediationScoreCofField,
  ReportConfig,
} from '@/__generated__/graphql';
import { REPORT_CONFIG_KEYS } from './constants';

type Props = {
  atom?: PrimitiveAtom<SnackbarMessage>;
  variant?: 'filled' | 'standard' | 'outlined';
};

export const useSnackBarMessage = ({
  atom = TemplateBuilderSnackbarMessage,
  variant = 'filled',
}: Props) => {
  const setSnackbarMessage = useSetAtom(atom);
  const showSnackBar = (message: string, severity: AlertColor) => {
    setSnackbarMessage({
      shouldShow: true,
      content: (
        <Alert sx={{ color: 'white' }} variant={variant} severity={severity}>
          {message && (
            <>
              {message}
              &nbsp;&nbsp;
            </>
          )}
        </Alert>
      ),
    });
  };

  return {
    showSnackBar,
  };
};

export const stripTypenameFromRemediationConfig = (
  remediationScoreConfig: RemediationScoreConfig
): RemediationScoreConfig => {
  return {
    area: remediationScoreConfig.area
      ? {
          fields: remediationScoreConfig.area.fields?.map((field: RemediationScoreAreaField) => ({
            fieldKey: field.fieldKey,
            threshold: field.threshold,
            score: field.score,
          })),
        }
      : null,
    poi: remediationScoreConfig.poi
      ? {
          fields: remediationScoreConfig.poi.fields?.map((field: RemediationScorePoiField) => ({
            poiTemplateKey: field.poiTemplateKey,
            poiScore: field.poiScore,
            subcategory: field.subcategory
              ? {
                  fieldKey: field.subcategory.fieldKey,
                  optionScores: field.subcategory.optionScores,
                }
              : undefined,
          })),
        }
      : null,
    cof: remediationScoreConfig.cof
      ? {
          fields: remediationScoreConfig.cof.fields?.map((field: RemediationScoreCofField) => ({
            fieldKey: field.fieldKey,
            optionScores: field.optionScores,
          })),
        }
      : null,
  };
};

export const convertToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.addEventListener('load', () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Result of file reading was not a string.'));
      }
    });
    reader.addEventListener('error', () => reject(reader.error));
  });

export const orderReportConfig = (config: ReportConfig['configurations']) => {
  const keyIndex: Record<string, number> = REPORT_CONFIG_KEYS.reduce(
    (accumulator, key, index) => ({ ...accumulator, [key]: index }),
    {}
  );

  return [...(config ?? [])].sort((a, b) => {
    const ai = keyIndex[a.key] ?? Infinity;
    const bi = keyIndex[b.key] ?? Infinity;
    return ai - bi;
  });
};

export const removeTypename = <T,>(obj: T): T => {
  if (Array.isArray(obj)) {
    return (obj as unknown[]).map((item) => removeTypename(item)) as unknown as T;
  }

  if (obj !== null && typeof obj === 'object') {
    if (obj instanceof Date) return obj;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { __typename, ...rest } = obj as Record<string, unknown>;

    const cleaned = Object.fromEntries(
      Object.entries(rest).map(([key, value]) => [key, removeTypename(value as unknown)])
    );

    return cleaned as T;
  }

  return obj;
};

export const titleToKey = (title: string) => title.trim().toLowerCase().replace(/\s+/g, '_');
